import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Box
} from '@mui/material';
import * as dateFns from 'date-fns';
import tracker from '../../api/tracker';
// components
import { ConfirmationDialog } from '../../components/general/ConfirmationDialog';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import Iconify from '../../components/Iconify';
import SearchNotFound from '../../components/SearchNotFound';
import useQuery from '../../hooks/useQuery';
import { ListHead, ListToolbar, MoreMenu } from '../../components/tables';
import { useApi } from '../../hooks/useApi';
import ConstructionAssistanceModal from '../../sections/@dashboard/constructions/AssistanceByConstructionModal';

const override = {
  margin: '0 auto',
};

export default function ConstructionIndex() {
  const [constructions, setConstructions] = useState([]);
  const [selectedConstruction, setSelectedConstruction] = useState();
  const [pagination, setPagination] = useState({ total: 0, per_page: 0, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState('asc');
  const [sortField, setSortField] = useState('name');
  const [selected, setSelected] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const { doDelete } = useApi();
  const [loading, setLoading] = useState(true);

  const fetchData = useQuery();
  const { t } = useTranslation();

  const TABLE_HEAD = [
    { id: 'name', label: t('Name'), align: 'left' },
    { id: 'city', label: t('City'), align: 'left' },
    { id: 'address', label: t('Address'), align: 'left' },
    { id: 'see_attendance', label: t('see_attendance'), align: 'center', sortable: false },
    { id: 'actions', label: t('actions'), align: 'center', sortable: false },
  ];

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('construction_sites', filter, sort, setConstructions, sortField, setPagination, page, setNotFound);
    setIsLoading(false);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(refreshTable, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [filter, sort, page, sortField]);

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = constructions.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleDeleteDialog = (construction) => {
    setSelectedConstruction(construction);
    setShowDeleteDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async () => {
    setLoading(true);
    doDelete(`constructions/${selectedConstruction.id}`, t('delete_success'), t('delete_error'), refreshTable);
    setLoading(false);
    setShowDeleteDialog(false);
    setSelected([]);
  };

  const handleMassDelete = async () => {
    setLoading(true);
    doDelete(
      `constructions/mass`,
      t('delete_mass_success'),
      t('delete_mass_error'),
      refreshTable,
      { ids: selected },
      true
    );
    setLoading(false);
    setSelected([]);
  };

  const handleFilterByName = (event) => {
    setFilter(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - constructions.length) : 0;

  return (
    <Page title={t('Constructions')}>
      <ConstructionAssistanceModal
        isShowing={showCalendarModal}
        constructionSite={selectedConstruction}
        onClose={() => {
          setShowCalendarModal(false);
          setSelectedConstruction(selectedConstruction);
        }}
      />
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {t('Constructions')}
          </Typography>
        </Stack>
        {/* Filter section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, mb: 3, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ ml: 3 }}>
            {t('listaDeObras')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <ListToolbar
              numSelected={selected.length}
              filterName={filter}
              onFilterName={handleFilterByName}
              placeholder={`search_constructions`}
              handleMassDelete={handleMassDelete}
            />
            <Button
              variant="contained"
              component={RouterLink}
              to="/dashboard/constructions/save"
              startIcon={<Iconify icon="eva:plus-fill" />}
              size="small"
              sx={{ height: 40 }}
            >
              {t('new_construction')}
            </Button>
          </Box>
        </Box>
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                {isLoading ? (
                  <FadeLoader color={'blue'} loading={loading} cssOverride={override} size={50} />
                ) : (
                  <>
                    <ListHead
                      headLabel={TABLE_HEAD}
                      rowCount={constructions.length}
                      order={sort}
                      orderBy={sortField}
                      omitCheckbox
                      onRequestSort={setSortField}
                      setSort={setSort}
                    />
                    <TableBody>
                      {constructions.map((row) => {
                        const { name, id, address, city } = row;
                        const isItemSelected = selected.indexOf(id) !== -1;
                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            city="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell component="th" scope="row" padding="none" sx={{ pl: 2 }}>
                              <Typography variant="subtitle2" noWrap sx={{ paddingLeft: 2 }}>
                                {name}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">{t(city)}</TableCell>
                            <TableCell align="left">{row.address}</TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                              <IconButton
                                size="small"
                                sx={{ p: 0, position: 'relative', top: '50%', transform: 'translateY(-50%)' }}
                                onClick={() => {
                                  setSelectedConstruction(row);
                                  setShowCalendarModal(true);
                                }}
                              >
                                <Iconify icon="solar:calendar-bold-duotone" width={20} height={20} />
                              </IconButton>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton component={RouterLink} to={`/dashboard/attendance_photos/${id}`} sx={{ mr: 1 }}>
                                <Iconify icon="eva:camera-fill" width={20} height={20} />
                              </IconButton>
                              <IconButton component={RouterLink} to={`/dashboard/constructions/edit/${id}`}>
                                <Iconify icon="eva:edit-fill" width={20} height={20} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>

                    {notFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                            <SearchNotFound searchQuery={filter} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <ConfirmationDialog
            open={showDeleteDialog}
            onClose={() => {
              setShowDeleteDialog(false);
            }}
            onConfirm={handleDelete}
            message={t('delete_confirm')}
          />
        </Card>
      </Container>
    </Page>
  );
}
